import { PHASE_OPTIONS } from '../ui/halftime/phaseOptions';
import type { HypothesisPhase, Status } from '../types';

/**
 * B — **the phase's reading label, once**.
 *
 * `PHASE_OPTIONS` (`src/ui/halftime/phaseOptions.ts`) is the one place the seven phases and their
 * labels are declared, and B imports it whole rather than mirroring it (the `useHalftime` /
 * `useFlags` precedent). What B kept re-deriving was the *lookup*: four surfaces had grown their
 * own two-line `Object.fromEntries(PHASE_OPTIONS…)` map, which is the shape the reuse contract's
 * duplication detector exists to catch — four copies of one derivation, each able to drift into a
 * different fallback for an unmapped phase.
 *
 * This is that derivation, in one module. B-16 moved itself and `AdjustmentsWorkflowB` onto it and
 * logged the remaining two as duplication-ledger candidates; **B-20 closed them** — `HypothesisSheetB`
 * and `SetupScreen` now call `phaseLabelB` too, so there is one derivation and no fallback to
 * disagree about. (`SetupScreen`'s copy was a `Map`, whose `.get()` returned `string | undefined`
 * where this returns `string`: two copies of one derivation had already produced two different
 * answers for an unmapped phase, which is the drift the single module exists to prevent.)
 *
 * A `Record` over the union: a phase added to the enum and missing from `PHASE_OPTIONS` is already
 * a compile error there, so this map cannot silently lose one.
 */

const PHASE_LABEL_B = Object.fromEntries(
  PHASE_OPTIONS.map((option) => [option.phase, option.label]),
) as Record<HypothesisPhase, string>;

/** The phase's label in reading order vocabulary — "Attacking transition", never `attTransition`. */
export function phaseLabelB(phase: HypothesisPhase): string {
  return PHASE_LABEL_B[phase];
}

/**
 * §2.3's four status words, once.
 *
 * The same argument as the phase map one function up: B-18's match record needs to *name* a
 * status, `HypothesisSheetB` already named one, and two copies of a four-word vocabulary is how a
 * detail sheet comes to say *Invalidated* where a record says *Ruled out*. A `Record` over the
 * union, so a fifth status cannot be added without answering what it is called.
 *
 * Status is never carried by these words alone — §2.3 pairs every status with a second channel —
 * but the word is what a record read weeks later is actually read for.
 */
const STATUS_LABEL_B: Record<Status, string> = {
  tracking: 'Tracking',
  confirmed: 'Confirmed',
  disproved: 'Disproved',
  invalidated: 'Invalidated',
};

export function statusLabelB(status: Status): string {
  return STATUS_LABEL_B[status];
}
